import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, FileText, Activity, TrendingUp } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const ReportsPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [financialData, setFinancialData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [RechartsComponents, setRechartsComponents] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, monthlyRes, transactionsRes] = await Promise.all([
          api.get("/stats", token),
          api.get("/reports/monthly", token),
          api.get("/transactions", token)
        ]);
        setStats(statsRes.data);
        setMonthlyData(monthlyRes.data);
        
        const transactions = transactionsRes.data || [];
        const grouped = {};
        transactions.forEach(t => {
          const date = t.date?.substring(0, 7) || "غير محدد";
          if (!grouped[date]) grouped[date] = { income: 0, expense: 0 };
          if (t.transaction_type === "income") grouped[date].income += t.amount || 0;
          else grouped[date].expense += t.amount || 0;
        });
        
        const chartData = Object.entries(grouped)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .slice(-6)
          .map(([month, data]) => ({
            month: month,
            الإيرادات: data.income,
            المصروفات: data.expense,
            الصافي: data.income - data.expense
          }));
        setFinancialData(chartData);
      } catch (error) {
        toast.error("خطأ في تحميل التقارير");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  useEffect(() => {
    import('recharts').then(module => {
      setRechartsComponents({
        BarChart: module.BarChart,
        Bar: module.Bar,
        XAxis: module.XAxis,
        YAxis: module.YAxis,
        CartesianGrid: module.CartesianGrid,
        Tooltip: module.Tooltip,
        Legend: module.Legend,
        ResponsiveContainer: module.ResponsiveContainer,
        LineChart: module.LineChart,
        Line: module.Line
      });
    });
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse-soft text-gray-500">جاري التحميل...</div></div>;
  }

  return (
    <div className="animate-fade-in" data-testid="reports-page">
      <div className="page-header">
        <h1 className="page-title">التقارير والإحصائيات</h1>
        <p className="page-subtitle">نظرة شاملة على أداء العيادة</p>
      </div>

      <div className="tabs mb-6">
        <button className={`tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>
          نظرة عامة
        </button>
        <button className={`tab ${activeTab === "financial" ? "active" : ""}`} onClick={() => setActiveTab("financial")}>
          التقارير المالية
        </button>
        <button className={`tab ${activeTab === "commissions" ? "active" : ""}`} onClick={() => setActiveTab("commissions")}>
          تقرير العمولات
        </button>
      </div>

      {activeTab === "overview" && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon bg-blue-100 text-blue-600">
                <Users className="w-6 h-6" />
              </div>
              <div className="stat-value">{stats?.total_patients || 0}</div>
              <div className="stat-label">إجمالي المرضى</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon bg-emerald-100 text-emerald-600">
                <FileText className="w-6 h-6" />
              </div>
              <div className="stat-value">{stats?.total_sessions || 0}</div>
              <div className="stat-label">إجمالي الجلسات</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon bg-amber-100 text-amber-600">
                <Activity className="w-6 h-6" />
              </div>
              <div className="stat-value">{stats?.completed_sessions || 0}</div>
              <div className="stat-label">الجلسات المكتملة</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon bg-purple-100 text-purple-600">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div className="stat-value">{stats?.new_patients_this_month || 0}</div>
              <div className="stat-label">مرضى جدد هذا الشهر</div>
            </div>
          </div>

          <div className="data-table">
            <div className="table-header">
              <h3 className="table-title">التقرير الشهري</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-right py-3 px-4 font-medium text-gray-500">الشهر</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">عدد الجلسات</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">البصيلات المزروعة</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">الإيرادات</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-8 text-gray-500">لا توجد بيانات</td>
                    </tr>
                  ) : (
                    monthlyData.map((row, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`report-row-${index}`}>
                        <td className="py-3 px-4 font-medium">{row.month}</td>
                        <td className="py-3 px-4">{row.sessions}</td>
                        <td className="py-3 px-4">{row.grafts}</td>
                        <td className="py-3 px-4">{row.revenue}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === "financial" && (
        <div className="space-y-6">
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">الإيرادات والمصروفات (آخر 6 أشهر)</h3>
            {RechartsComponents && financialData.length > 0 ? (
              <div style={{ width: '100%', height: 350, direction: 'ltr' }}>
                <RechartsComponents.ResponsiveContainer>
                  <RechartsComponents.BarChart data={financialData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <RechartsComponents.CartesianGrid strokeDasharray="3 3" />
                    <RechartsComponents.XAxis dataKey="month" />
                    <RechartsComponents.YAxis />
                    <RechartsComponents.Tooltip formatter={(value) => value.toLocaleString()} />
                    <RechartsComponents.Legend />
                    <RechartsComponents.Bar dataKey="الإيرادات" fill="#10B981" />
                    <RechartsComponents.Bar dataKey="المصروفات" fill="#EF4444" />
                  </RechartsComponents.BarChart>
                </RechartsComponents.ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">لا توجد بيانات مالية كافية لعرض الرسم البياني</div>
            )}
          </div>

          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">صافي الربح الشهري</h3>
            {RechartsComponents && financialData.length > 0 ? (
              <div style={{ width: '100%', height: 300, direction: 'ltr' }}>
                <RechartsComponents.ResponsiveContainer>
                  <RechartsComponents.LineChart data={financialData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <RechartsComponents.CartesianGrid strokeDasharray="3 3" />
                    <RechartsComponents.XAxis dataKey="month" />
                    <RechartsComponents.YAxis />
                    <RechartsComponents.Tooltip formatter={(value) => value.toLocaleString()} />
                    <RechartsComponents.Legend />
                    <RechartsComponents.Line type="monotone" dataKey="الصافي" stroke="#3B82F6" strokeWidth={3} dot={{ fill: '#3B82F6', strokeWidth: 2, r: 6 }} />
                  </RechartsComponents.LineChart>
                </RechartsComponents.ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">لا توجد بيانات</div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-emerald-600">
                {financialData.reduce((sum, d) => sum + d.الإيرادات, 0).toLocaleString()}
              </div>
              <div className="text-gray-500">إجمالي الإيرادات</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-red-600">
                {financialData.reduce((sum, d) => sum + d.المصروفات, 0).toLocaleString()}
              </div>
              <div className="text-gray-500">إجمالي المصروفات</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {financialData.reduce((sum, d) => sum + d.الصافي, 0).toLocaleString()}
              </div>
              <div className="text-gray-500">صافي الربح</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "commissions" && (
        <CommissionsReport token={token} />
      )}
    </div>
  );
};

// Commissions Report Component
const CommissionsReport = ({ token }) => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommissions();
  }, [token]);

  const fetchCommissions = async () => {
    try {
      const res = await api.get("/patients", token);
      const patients = res.data || [];
      
      const staffCommissions = {};
      
      patients.forEach(p => {
        if (p.extraction_by && p.extraction_cost) {
          if (!staffCommissions[p.extraction_by]) staffCommissions[p.extraction_by] = { name: p.extraction_by, total: 0, count: 0 };
          staffCommissions[p.extraction_by].total += p.extraction_cost;
          staffCommissions[p.extraction_by].count++;
        }
        if (p.channel_opening_by && p.channels_cost) {
          if (!staffCommissions[p.channel_opening_by]) staffCommissions[p.channel_opening_by] = { name: p.channel_opening_by, total: 0, count: 0 };
          staffCommissions[p.channel_opening_by].total += p.channels_cost;
          staffCommissions[p.channel_opening_by].count++;
        }
        ["transplant_1", "transplant_2", "transplant_3"].forEach(field => {
          if (p[field] && p.planting_cost) {
            const share = p.planting_cost / 3;
            if (!staffCommissions[p[field]]) staffCommissions[p[field]] = { name: p[field], total: 0, count: 0 };
            staffCommissions[p[field]].total += share;
            staffCommissions[p[field]].count++;
          }
        });
      });
      
      setCommissions(Object.values(staffCommissions).sort((a, b) => b.total - a.total));
    } catch (error) {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const totalCommissions = commissions.reduce((sum, c) => sum + c.total, 0);

  if (loading) {
    return <div className="text-center py-8 text-gray-500">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="card p-4">
        <div className="text-center">
          <div className="text-3xl font-bold text-emerald-600">{totalCommissions.toLocaleString()}</div>
          <div className="text-gray-500">إجمالي العمولات</div>
        </div>
      </div>

      <div className="data-table">
        <div className="table-header">
          <h3 className="table-title">تفاصيل العمولات حسب الموظف</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-right py-3 px-4 font-medium text-gray-500">اسم الموظف</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">عدد العمليات</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">إجمالي العمولة</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">النسبة</th>
              </tr>
            </thead>
            <tbody>
              {commissions.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-gray-500">لا توجد بيانات عمولات</td>
                </tr>
              ) : (
                commissions.map((c, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{c.name}</td>
                    <td className="py-3 px-4">{c.count}</td>
                    <td className="py-3 px-4 text-emerald-600 font-semibold">{c.total.toLocaleString()}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-emerald-500 h-2 rounded-full" 
                            style={{ width: `${totalCommissions > 0 ? (c.total / totalCommissions * 100) : 0}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500">
                          {totalCommissions > 0 ? (c.total / totalCommissions * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
